import React from "react";

const GooglePagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;

  // Build the page number window (Google shows ~10 numbers)
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 10;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    // Always show first page
    pages.push(1);

    // Calculate range around current page
    let start = Math.max(2, currentPage - 4);
    let end = Math.min(totalPages - 1, currentPage + 4);

    // Adjust if near the beginning
    if (currentPage <= 5) {
      start = 2;
      end = Math.min(totalPages - 1, 9);
    }
    // Adjust if near the end
    if (currentPage >= totalPages - 4) {
      start = Math.max(2, totalPages - 8);
      end = totalPages - 1;
    }

    // Add left ellipsis
    if (start > 2) pages.push("...");

    for (let i = start; i <= end; i++) pages.push(i);

    // Add right ellipsis
    if (end < totalPages - 1) pages.push("...");

    // Always show last page
    pages.push(totalPages);

    return pages;
  };

  const pageNumbers = getPageNumbers();

  const btnBase = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "36px",
    height: "36px",
    padding: "0 4px",
    border: "none",
    borderRadius: "50%",
    background: "transparent",
    cursor: "pointer",
    fontSize: "14px",
    fontFamily: "Arial, sans-serif",
    transition: "background 0.15s ease",
    userSelect: "none",
  };

  const navBtn = (disabled) => ({
    ...btnBase,
    color: disabled ? "#9aa0a6" : "#1a73e8",
    cursor: disabled ? "default" : "pointer",
    fontSize: "13px",
    fontWeight: "500",
    letterSpacing: "0.01em",
    borderRadius: "4px",
    padding: "0 10px",
    gap: "4px",
  });

  const numBtn = (isActive) => ({
    ...btnBase,
    color: isActive ? "#1a73e8" : "#3c4043",
    fontWeight: isActive ? "700" : "400",
    fontSize: isActive ? "15px" : "14px",
    textDecoration: isActive ? "underline" : "none",
    textDecorationColor: isActive ? "#1a73e8" : "transparent",
    background: "transparent",
  });

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "2px",
        // padding: "12px 0",
        fontFamily: "Arial, sans-serif",
        flexWrap: "wrap",
      }}
    >
      {/* Previous Button */}
      <button
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
        style={navBtn(currentPage === 1)}
        onMouseEnter={(e) => {
          if (currentPage !== 1) e.currentTarget.style.background = "#f1f3f4";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
        }}
        aria-label="Previous page"
      >
        {/* Left arrow SVG */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6z" />
        </svg>
        <span>Previous</span>
      </button>

      {/* Page Numbers */}
      {pageNumbers.map((page, idx) =>
        page === "..." ? (
          <span
            key={`ellipsis-${idx}`}
            style={{
              minWidth: "36px",
              height: "36px",
              display: "inline-flex",
              alignItems: "flex-end",
              justifyContent: "center",
              color: "#3c4043",
              fontSize: "14px",
              paddingBottom: "6px",
              letterSpacing: "2px",
            }}
          >
            ...
          </span>
        ) : (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            style={numBtn(page === currentPage)}
            onMouseEnter={(e) => {
              if (page !== currentPage)
                e.currentTarget.style.background = "#f1f3f4";
            }}
            onMouseLeave={(e) => {
              if (page !== currentPage)
                e.currentTarget.style.background = "transparent";
            }}
            aria-label={`Page ${page}`}
            aria-current={page === currentPage ? "page" : undefined}
          >
            {page}
          </button>
        ),
      )}

      {/* Next Button */}
      <button
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
        style={navBtn(currentPage === totalPages)}
        onMouseEnter={(e) => {
          if (currentPage !== totalPages)
            e.currentTarget.style.background = "#f1f3f4";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
        }}
        aria-label="Next page"
      >
        <span>Next</span>
        {/* Right arrow SVG */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z" />
        </svg>
      </button>
    </div>
  );
};

export default GooglePagination;
